import { Environment, Url } from '@atlassiansox/bxpkit-core';
import OriginTracing from '@atlassiansox/origin-tracing';
import styled from '@emotion/styled';
import React from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { SocialSignonButtonOptionsType } from '../../../../../model';
import { SocialSignUpButton as SocialSignUpButtonLegacy } from '../../social-sign-up-button';
import { SocialSignUpButton } from '../../../../../layouts/simple-signup-screen/simple-signup-form-view/component/social-sign-up-button';
import { messages } from '../messages';
import { useSignUpFormConfig } from '../../../../../hook';
import { useAppSelector } from '../../../../../redux';
import { N60, N500, N400, N90, B400 } from '@atlaskit/theme/colors';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { messages as signupMessages } from '../../../../../messages/sign-up-form';

/**
 * Render social signup buttons
 */

export function SignupButtons({
  analyticsTrack,
  isEmailSignupSelected,
  setIsEmailSignupSelected,
  allowSignupWithAllSocialOptions
}: SocialSignonButtonOptionsType) {
  const { segment } = analyticsTrack;
  const trySignupOrigin = new OriginTracing({ product: 'wac-try-signup' });
  const loginGoogleOrigin = new OriginTracing({ product: 'wac-signup-google' });

  const signUpFormConfig = useSignUpFormConfig();
  const queryParameters = Url.getUrlParams() || {};

  const continueUrl = new URL(window.location.href).pathname;

  const urlGoogleSignupContinue = Environment.getSocialSignUpUrl({
    loginGoogleOrigin: loginGoogleOrigin.encode(),
    socialContinueUrl: continueUrl || '',
    ...signUpFormConfig,
    continueUrlOverride: queryParameters['continue'] || undefined
  });

  const isConfluenceInBundle = signUpFormConfig?.bundle?.includes('confluence');
  const isConfluenceProduct =
    signUpFormConfig?.product === 'confluence.ondemand';
  const isConfluence = isConfluenceInBundle || isConfluenceProduct;

  const { enableDefaultExpandedEmail } = useAppSelector(
    (state) => state.featureFlags
  );

  const { createAnalyticsEvent } = useAnalyticsEvents();

  if (allowSignupWithAllSocialOptions && !isConfluence) {
    const redirectUrl = window.location.href;
    return (
      <SocialButtonsGridWrapper>
        <div className="long-dash-wrapper">
          <span aria-hidden />
          <FormattedMessage
            {...messages.socialSignUpOrSignUpWith}
            tagName="p"
          />
          <span aria-hidden />
        </div>
        <div>
          <SocialSignUpButton
            buttonText="Google"
            provider="google"
            redirectUrl={redirectUrl}
          />
          <SocialSignUpButton
            buttonText="Microsoft"
            provider="microsoft"
            redirectUrl={redirectUrl}
          />
          <SocialSignUpButton
            buttonText="Apple"
            provider="apple"
            redirectUrl={redirectUrl}
          />
          <SocialSignUpButton
            buttonText="Slack"
            provider="slack"
            redirectUrl={redirectUrl}
          />
        </div>
      </SocialButtonsGridWrapper>
    );
  }

  return (
    <div>
      {isConfluence && (
        <DividerChooseSignup>
          <FormattedMessage {...messages.socialSignUpOr} />
        </DividerChooseSignup>
      )}
      <SocialSignUpButtonLegacy
        buttonType="google"
        className="social-signup-button google"
        href={urlGoogleSignupContinue}
        onClick={() => {
          segment(
            'wac-signup-google',
            loginGoogleOrigin.toAnalyticsAttributes({ hasGeneratedId: true })
          );
        }}
      />

      {isConfluence &&
        !signUpFormConfig.experimentScreensFlags
          ?.enableSlackPromoConfluence && (
          <LoginButton
            onClick={() => {
              createAnalyticsEvent({
                event: 'buttonClicked',
                eventName: 'loginButtonClicked',
                eventType: 'UI',
                eventComponent: 'signup',
                eventColumn: 'signUp'
              }).fire();
            }}
          >
            <FormattedHTMLMessage {...signupMessages.loginChooseSignup} />
          </LoginButton>
        )}
      {!isConfluence &&
        (enableDefaultExpandedEmail ? (
          <DividerDefaultExpandedEmail>
            <Leftline />
            <FormattedMessage {...messages.socialSignUpOr} />
            <Rightline />
          </DividerDefaultExpandedEmail>
        ) : (
          <Divider>
            <Leftline />
            <FormattedMessage {...messages.socialSignUpOr} />
            <Rightline />
          </Divider>
        ))}

      {!enableDefaultExpandedEmail && !isEmailSignupSelected && !isConfluence && (
        <SocialSignUpButtonLegacy
          buttonType=""
          className={'social-signup-button email'}
          onClick={() => {
            if (setIsEmailSignupSelected) {
              setIsEmailSignupSelected(true);
            }
            segment(
              'wac-try-signup',
              trySignupOrigin.toAnalyticsAttributes({ hasGeneratedId: true })
            );
          }}
        />
      )}
    </div>
  );
}

const text =
  "Charlie Text, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Noto Sans', 'Ubuntu', 'Droid Sans', 'Helvetica Neue', sans-serif";

const Divider = styled.div`
  font-family: ${text};
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #42526e;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
`;
const DividerDefaultExpandedEmail = styled.div`
  font-family: ${text};
  margin-bottom: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #42526e;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
`;

const DividerChooseSignup = styled.div`
  font-family: ${text};
  margin-bottom: 40px;
  margin-top: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${N400};
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  text-transform: uppercase;
`;

const Leftline = styled.div`
  border-bottom: 1px solid #d8d8d8;
  width: 31px;
  margin-right: 8px;
`;
const Rightline = styled.div`
  border-bottom: 1px solid #d8d8d8;
  width: 31px;
  margin-left: 8px;
`;

const SocialButtonsGridWrapper = styled.div`
  padding-bottom: 68px;

  a {
    color: ${N500};
    font-weight: 400;
    margin-bottom: 0;

    + a {
      margin-top: 12px;
    }
  }

  .long-dash-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 24px 0;

    p {
      font-family: ${text};
      font-size: 16px;
      line-height: 1.5;
      margin: 0;
    }

    span {
      border-bottom: 1px solid ${N60};
      margin: 4px 12px 0;
      width: 33px;
    }
  }
`;
const LoginButton = styled.div`
  width: 100%;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  font-family: 'Charlie Text';
  text-align: center;
  margin-top: 60px;
  color: ${N90};
  a {
    color: ${B400};
    text-decoration: none;
    cursor: pointer;
  }
`;
